import React, { useState, useEffect } from "react";
import { Contract } from "@ethersproject/contracts";
import { useEthers } from "@usedapp/core";
import { addresses, abis } from "@my-app/contracts";
import {
  PositionsContainer,
  PositionCard,
  CardTitle,
  CardRow,
  CardLabel,
  CardValue,
  CancelButton,
  Tabs,
  Tab,
} from "./styles";

const UserPositions = () => {
  const { library, account } = useEthers();
  const [orders, setOrders] = useState({});
  const [executedOrders, setExecutedOrders] = useState(new Set());
  const [cancelledOrders, setCancelledOrders] = useState(new Set());
  const [activeTab, setActiveTab] = useState("pending");
  const [contract, setContract] = useState(null);

  useEffect(() => {
    if (library && account) {
      const signer = library.getSigner();
      const contractInstance = new Contract(
        addresses.positionManager,
        abis.positionManager,
        signer
      );
      setContract(contractInstance);
    }
  }, [library, account]);

  useEffect(() => {
    const fetchEvents = async () => {
      if (contract) {
        const addedFilter = contract.filters.PositionAdded();
        const addedEvents = await contract.queryFilter(
          addedFilter,
          0,
          "latest"
        );

        const removedFilter = contract.filters.PositionRemoved();
        const removedEvents = await contract.queryFilter(
          removedFilter,
          0,
          "latest"
        );
        const cancelled = new Set(
          removedEvents
            .filter((event) => event.args.creator === account)
            .map((event) => event.args.orderId.toString())
        );
        setCancelledOrders(cancelled);

        const executedFilter = contract.filters.PositionExecuted();
        const executedEvents = await contract.queryFilter(
          executedFilter,
          0,
          "latest"
        );
        const executed = new Set(
          executedEvents
            .filter((event) => event.args.creator === account)
            .map((event) => event.args.orderId.toString())
        );
        setExecutedOrders(executed);

        const activePositions = addedEvents
          .filter((event) => event.args.creator === account)
          .map((event) => event.args.orderId.toString())
          .filter((id) => !executed.has(id) && !cancelled.has(id));

        const ordersData = await Promise.all(
          activePositions.map((orderId) =>
            contract.orderToPositionMapping(orderId)
          )
        );

        const ordersDict = {};
        ordersData.forEach((order, index) => {
          ordersDict[activePositions[index]] = order;
        });

        setOrders(ordersDict);
      }
    };

    fetchEvents();
  }, [contract, account]);

  const cancelOrder = async (orderId) => {
    try {
      await contract.removePosition(orderId);
      setCancelledOrders(new Set([...cancelledOrders, orderId]));
    } catch (error) {
      console.error("Failed to cancel order", error);
    }
  };

  const filterOrdersByStatus = (status) => {
    switch (status) {
      case "executed":
        return Array.from(executedOrders);
      case "cancelled":
        return Array.from(cancelledOrders);
      case "pending":
      default:
        return Object.keys(orders).filter(
          (orderId) =>
            !executedOrders.has(orderId) && !cancelledOrders.has(orderId)
        );
    }
  };

  return (
    <PositionsContainer>
      <Tabs>
        <Tab
          onClick={() => setActiveTab("pending")}
          active={activeTab === "pending"}
        >
          Pending
        </Tab>
        <Tab
          onClick={() => setActiveTab("executed")}
          active={activeTab === "executed"}
        >
          Executed
        </Tab>
        <Tab
          onClick={() => setActiveTab("cancelled")}
          active={activeTab === "cancelled"}
        >
          Cancelled
        </Tab>
      </Tabs>
      <CardTitle>Your Positions</CardTitle>
      {filterOrdersByStatus(activeTab).map((orderId, index) => (
        <PositionCard key={index}>
          <CardRow>
            <CardLabel>Order ID:</CardLabel>
            <CardValue>{orderId}</CardValue>
          </CardRow>
          {orders[orderId] && (
            <>
              <CardRow>
                <CardLabel>Token In:</CardLabel>
                <CardValue>{orders[orderId].tokenIn}</CardValue>
              </CardRow>
              <CardRow>
                <CardLabel>Token Out:</CardLabel>
                <CardValue>{orders[orderId].tokenOut}</CardValue>
              </CardRow>
              <CardRow>
                <CardLabel>Amount:</CardLabel>
                <CardValue>{orders[orderId].amountIn.toString()}</CardValue>
              </CardRow>
              <CardRow>
                <CardLabel>Desired Price:</CardLabel>
                <CardValue>{orders[orderId].price.toString()}</CardValue>
              </CardRow>
            </>
          )}
          {activeTab === "pending" && (
            <CancelButton onClick={() => cancelOrder(orderId)}>
              Cancel Order
            </CancelButton>
          )}
        </PositionCard>
      ))}
    </PositionsContainer>
  );
};

export default UserPositions;
